import React, { useContext, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import CssBaseline from "@mui/material/CssBaseline";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Link from "@mui/material/Link";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CryptoJS from "crypto-js";
import { useNavigate } from "react-router-dom";
import { ContextSession } from "../../services/ContextSession";
import logo from "./assets/nuevo Logo.png";

const BRAND_PRIMARY = "#19438C";
const BRAND_PRIMARY_DARK = "#153873";
const SURFACE_SOFT = "#F8FAFD";
const SURFACE_PAGE = "#F4F7FB";

const defaultTheme = createTheme({
  palette: {
    primary: {
      main: BRAND_PRIMARY,
    },
  },
});

const textFieldStyles = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 3,
    backgroundColor: SURFACE_SOFT,
    "& fieldset": {
      borderColor: "rgba(25, 67, 140, 0.16)",
    },
    "&:hover fieldset": {
      borderColor: BRAND_PRIMARY,
    },
    "&.Mui-focused fieldset": {
      borderColor: BRAND_PRIMARY,
      borderWidth: 2,
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: BRAND_PRIMARY,
  },
};

function Copyright(props) {
  return (
    <Typography
      variant="body2"
      color="text.secondary"
      align="center"
      {...props}
    >
      {"Copyright © "}
      <Link color="inherit" href="https://www.injupemp.gob.hn/">
        INJUPEMP
      </Link>{" "}
      {new Date().getFullYear()}
      {"."}
    </Typography>
  );
}

export default function CambiarPassword() {
  const sessi = useContext(ContextSession);
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwords, setPasswords] = useState({
    actual: "",
    nueva: "",
    confirmar: "",
  });
  const [error, setError] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleClickShowPassword = (type) => {
    if (type === "actual") {
      setShowPassword(!showPassword);
    } else if (type === "nueva") {
      setShowNewPassword(!showNewPassword);
    } else if (type === "confirmar") {
      setShowConfirmPassword(!showConfirmPassword);
    }
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleChange = (event) => {
    const { id, value } = event.target;
    setPasswords({ ...passwords, [id]: value });
  };

  const getBackendErrorMessage = (errorData) => {
    if (!errorData) {
      return "Error al cambiar la contraseña";
    }

    if (typeof errorData === "string") {
      return errorData;
    }

    return (
      errorData.message ||
      errorData.detail ||
      errorData.error ||
      errorData.non_field_errors?.[0] ||
      errorData.passs_nuevo?.[0] ||
      errorData.passs_anterior?.[0] ||
      errorData.usuario?.[0] ||
      "Error al cambiar la contraseña"
    );
  };

  const validatePassword = (password) => {
    const hasMinimumLength = password.length >= 8;
    const hasUppercase = /[A-Z]/.test(password);
    const hasNumber = /\d/.test(password);

    if (!hasMinimumLength && !hasUppercase && !hasNumber) {
      return "La contraseña nueva debe tener al menos 8 caracteres, una letra mayúscula y un dígito numérico.";
    }

    if (!hasMinimumLength && !hasUppercase) {
      return "La contraseña nueva debe tener al menos 8 caracteres y una letra mayúscula.";
    }

    if (!hasMinimumLength && !hasNumber) {
      return "La contraseña nueva debe tener al menos 8 caracteres y un dígito numérico.";
    }

    if (!hasUppercase && !hasNumber) {
      return "La contraseña nueva debe contener al menos una letra mayúscula y un dígito numérico.";
    }

    if (!hasMinimumLength) {
      return "La contraseña nueva debe tener al menos 8 caracteres.";
    }

    if (!hasUppercase) {
      return "La contraseña nueva debe contener al menos una letra mayúscula.";
    }

    if (!hasNumber) {
      return "La contraseña nueva debe contener al menos un dígito numérico.";
    }

    return null;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const { actual, nueva, confirmar } = passwords;

    if (!actual || !nueva || !confirmar) {
      setError("Todos los campos son obligatorios");
      return;
    }

    if (nueva !== confirmar) {
      setError("Las contraseñas no coinciden.");
      return;
    }

    const passwordError = validatePassword(nueva);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    setError("");
    setLoading(true);

    const be = "2PQ2WI6oAxDYAaRDegWOsg9smJclabbYi7y3nFivisTBaDlHlE";
    const encryptAES = (data, key) => {
      return CryptoJS.AES.encrypt(data, key).toString();
    };

    const encryptedEmail = encryptAES(sessi.sessionData.usuario, be);
    const encryptedPassActual = encryptAES(actual, be);
    const encryptedPassNueva = encryptAES(nueva, be);

    try {
      const response = await fetch(`${sessi.ip}/api/part/cambiarpass/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          usuario: encryptedEmail,
          passs_anterior: encryptedPassActual,
          passs_nuevo: encryptedPassNueva,
        }),
      });

      if (response.ok) {
        sessi.clearSession();
        localStorage.clear();
        sessionStorage.clear();
        setOpenDialog(true);
      } else {
        const errorData = await response.json();
        setError(getBackendErrorMessage(errorData));
      }
    } catch (requestError) {
      console.error("Error:", requestError);
      setError("Error al conectar con el servidor");
    }

    setLoading(false);
  };

  return (
    <ThemeProvider theme={defaultTheme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
          py: { xs: 3, md: 5 },
          backgroundColor: SURFACE_PAGE,
        }}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 0, sm: 2 } }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "0.94fr 1.06fr" },
              borderRadius: { xs: 4, md: 6 },
              overflow: "hidden",
              backgroundColor: "white",
              boxShadow: "0 24px 60px rgba(15, 32, 69, 0.22)",
            }}
          >
            <Box
              sx={{
                position: "relative",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                gap: 2.5,
                p: { xs: 4, sm: 5, md: 6 },
                color: "white",
                background: `linear-gradient(180deg, ${BRAND_PRIMARY} 0%, #2857A6 100%)`,
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: -80,
                  right: -40,
                  width: 220,
                  height: 220,
                  borderRadius: "50%",
                  backgroundColor: "rgba(255,255,255,0.08)",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  bottom: -70,
                  left: -55,
                  width: 180,
                  height: 180,
                  borderRadius: "50%",
                  backgroundColor: "rgba(255,255,255,0.07)",
                }}
              />
              <Box sx={{ position: "relative", zIndex: 1 }}>
                <Box
                  component="img"
                  src={logo}
                  alt="Logo institucional INJUPEMP"
                  sx={{
                    width: { xs: "100%", sm: 360 },
                    maxWidth: "100%",
                    height: "auto",
                    mb: 4,
                  }}
                />
                <Typography
                  component="h1"
                  sx={{
                    fontSize: { xs: "2rem", md: "2.5rem" },
                    fontWeight: 800,
                    lineHeight: 1.1,
                  }}
                >
                  Protege tu acceso
                </Typography>
                <Typography
                  sx={{
                    mt: 2,
                    maxWidth: 460,
                    fontSize: { xs: "0.98rem", md: "1.05rem" },
                    lineHeight: 1.7,
                    color: "rgba(255,255,255,0.88)",
                  }}
                >
                  Actualiza tu contraseña para mantener segura tu cuenta y
                  continuar utilizando los servicios digitales.
                </Typography>
                <Box sx={{ mt: 4, display: "grid", gap: 1.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                    <CheckCircleIcon sx={{ fontSize: 20, color: "#A9D4FF" }} />
                    <Typography sx={{ fontSize: "0.96rem" }}>
                      Usa al menos 8 caracteres.
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                    <CheckCircleIcon sx={{ fontSize: 20, color: "#A9D4FF" }} />
                    <Typography sx={{ fontSize: "0.96rem" }}>
                      Incluye una letra mayúscula.
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                    <CheckCircleIcon sx={{ fontSize: 20, color: "#A9D4FF" }} />
                    <Typography sx={{ fontSize: "0.96rem" }}>
                      Agrega al menos un dígito numérico.
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                    <CheckCircleIcon sx={{ fontSize: 20, color: "#A9D4FF" }} />
                    <Typography sx={{ fontSize: "0.96rem" }}>
                      Confirma el cambio para ingresar nuevamente al sistema.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: { xs: 3, sm: 4, md: 6 },
                backgroundColor: "white",
              }}
            >
              <Box sx={{ width: "100%", maxWidth: 470 }}>
                <Typography
                  component="h2"
                  sx={{
                    fontSize: { xs: "2rem", md: "2.25rem" },
                    fontWeight: 800,
                    lineHeight: 1.15,
                    color: "#10233F",
                  }}
                >
                  Cambio de Contraseña
                </Typography>
                <Typography
                  sx={{
                    mt: 1.5,
                    color: "#5B6780",
                    fontSize: "0.98rem",
                    lineHeight: 1.7,
                  }}
                >
                  Ingresa tu contraseña actual y define una nueva credencial.
                </Typography>

                <Box
                  component="form"
                  noValidate
                  onSubmit={handleSubmit}
                  sx={{ mt: 4, display: "grid", gap: 2 }}
                >
                  <TextField
                    required
                    fullWidth
                    id="actual"
                    label="Contraseña Actual"
                    type={showPassword ? "text" : "password"}
                    value={passwords.actual}
                    onChange={handleChange}
                    sx={textFieldStyles}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => handleClickShowPassword("actual")}
                            onMouseDown={handleMouseDownPassword}
                          >
                            {showPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    required
                    fullWidth
                    id="nueva"
                    label="Nueva Contraseña"
                    type={showNewPassword ? "text" : "password"}
                    value={passwords.nueva}
                    onChange={handleChange}
                    sx={textFieldStyles}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => handleClickShowPassword("nueva")}
                            onMouseDown={handleMouseDownPassword}
                          >
                            {showNewPassword ? (
                              <Visibility />
                            ) : (
                              <VisibilityOff />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    required
                    fullWidth
                    id="confirmar"
                    label="Confirmar Nueva Contraseña"
                    type={showConfirmPassword ? "text" : "password"}
                    value={passwords.confirmar}
                    onChange={handleChange}
                    sx={textFieldStyles}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => handleClickShowPassword("confirmar")}
                            onMouseDown={handleMouseDownPassword}
                          >
                            {showConfirmPassword ? (
                              <Visibility />
                            ) : (
                              <VisibilityOff />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  {error && (
                    <Alert
                      severity="warning"
                      sx={{
                        mt: 0.5,
                        borderRadius: 3,
                        alignItems: "center",
                      }}
                    >
                      {error}
                    </Alert>
                  )}

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    sx={{
                      mt: 1,
                      py: 1.6,
                      borderRadius: 3,
                      fontSize: "1rem",
                      fontWeight: 800,
                      letterSpacing: 0.3,
                      textTransform: "none",
                      backgroundColor: BRAND_PRIMARY,
                      boxShadow: "0 14px 30px rgba(25, 67, 140, 0.24)",
                      "&:hover": {
                        backgroundColor: BRAND_PRIMARY_DARK,
                      },
                    }}
                    disabled={loading}
                  >
                    {loading ? (
                      <CircularProgress size={24} sx={{ color: "white" }} />
                    ) : (
                      "Cambiar Contraseña"
                    )}
                  </Button>
                </Box>

                <Copyright sx={{ mt: 4 }} />
              </Box>
            </Box>
          </Box>
        </Container>

        <Dialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          PaperProps={{
            sx: {
              borderRadius: 4,
              px: 1,
              py: 1,
            },
          }}
        >
          <DialogTitle
            id="modal-title"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
            }}
          >
            <CheckCircleIcon
              color="success"
              sx={{ fontSize: 40, marginRight: 1 }}
            />
            Cambio de contraseña exitoso
          </DialogTitle>
          <DialogContent>
            <Typography>Gracias por utilizar nuestros servicios.</Typography>
          </DialogContent>
          <DialogActions
            sx={{
              display: "flex",
              justifyContent: "center",
              padding: 2,
            }}
          >
            <Button
              onClick={() => {
                setOpenDialog(false);
                navigate("/Login", { replace: true });
              }}
              sx={{
                px: 3,
                borderRadius: 3,
                color: "#fff",
                fontWeight: 700,
                backgroundColor: BRAND_PRIMARY,
                "&:hover": {
                  backgroundColor: BRAND_PRIMARY_DARK,
                },
              }}
            >
              Aceptar
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
}
